import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import { syncJunctionTable } from '../../../utils/syncJunctionTable';
import type { SettingsOptionRow } from '../types';

interface UseManageOptionsReturn {
  options: SettingsOptionRow[];
  archivedOptions: SettingsOptionRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  addOption: (name: string, categoryIds: string[]) => Promise<boolean>;
  updateOption: (id: string, name: string, categoryIds: string[]) => Promise<boolean>;
  archiveOption: (id: string) => Promise<boolean>;
  unarchiveOption: (id: string) => Promise<boolean>;
  deleteOption: (id: string) => Promise<'ok' | 'has_machines' | 'error'>;
}

async function buildRows(data: SettingsOptionRow[]): Promise<SettingsOptionRow[]> {
  const ids = data.map((o) => o.id);
  if (!ids.length) return [];
  const [{ data: machineOptionRows }, { data: templateOptionRows }, { data: categoryOptionRows }] = await Promise.all([
    supabase.from('machine_options').select('option_id').in('option_id', ids),
    supabase.from('machine_template_options').select('option_id').in('option_id', ids),
    supabase.from('category_options').select('option_id, category_id').in('option_id', ids),
  ]);
  const inUse = new Set([
    ...(machineOptionRows ?? []).map((m) => m.option_id),
    ...(templateOptionRows ?? []).map((t) => t.option_id),
  ]);
  const categoryIdsByOption: Record<string, string[]> = {};
  (categoryOptionRows ?? []).forEach((row: { option_id: string; category_id: string }) => {
    (categoryIdsByOption[row.option_id] ??= []).push(row.category_id);
  });
  return data.map((o) => ({
    ...o,
    has_machines: inUse.has(o.id),
    category_ids: categoryIdsByOption[o.id] ?? [],
  }));
}

export function useManageOptions(orgId: string | null): UseManageOptionsReturn {
  const [options, setOptions] = useState<SettingsOptionRow[]>([]);
  const [archivedOptions, setArchivedOptions] = useState<SettingsOptionRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const loadActive = async () => {
    if (!orgId) {
      setOptions([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('options')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('name');
      setOptions(await buildRows((data ?? []) as SettingsOptionRow[]));
    } catch {
      setOptions([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data } = await supabase
        .from('options')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('name');
      setArchivedOptions(await buildRows((data ?? []) as SettingsOptionRow[]));
      return true;
    } catch {
      setArchivedOptions([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedOptions([]);
    loadActive();
  }, [orgId]);

  const addOption = async (name: string, categoryIds: string[]): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data, error } = await supabase
        .from('options')
        .insert({
          name,
          organization_id: orgId,
          archived: false,
        })
        .select('id')
        .single();
      if (error) throw error;
      if (categoryIds.length > 0 && data) {
        const { error: linkError } = await supabase.from('category_options').insert(
          categoryIds.map((category_id) => ({ category_id, option_id: data.id, organization_id: orgId }))
        );
        if (linkError) throw linkError;
      }
      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateOption = async (id: string, name: string, categoryIds: string[]): Promise<boolean> => {
    try {
      const { error } = await supabase.from('options').update({ name }).eq('id', id);
      if (error) throw error;

      const previous = options.find((o) => o.id === id) ?? archivedOptions.find((o) => o.id === id);
      await syncJunctionTable({
        table: 'category_options',
        fixedColumn: 'option_id',
        fixedValue: id,
        varyingColumn: 'category_id',
        previousIds: previous?.category_ids ?? [],
        nextIds: categoryIds,
        organizationId: orgId,
      });

      const update = (list: SettingsOptionRow[]) =>
        sortByName(list.map((o) => (o.id === id ? { ...o, name, category_ids: categoryIds } : o)));
      setOptions(update);
      setArchivedOptions(update);
      return true;
    } catch {
      return false;
    }
  };

  const archiveOption = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('options').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setOptions((prev) => prev.filter((o) => o.id !== id));
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch { return false; }
  };

  const unarchiveOption = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('options').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedOptions((prev) => prev.filter((o) => o.id !== id));
      await loadActive();
      return true;
    } catch { return false; }
  };

  const deleteOption = async (id: string): Promise<'ok' | 'has_machines' | 'error'> => {
    try {
      const { count: machineOptionsCount } = await supabase
        .from('machine_options')
        .select('*', { count: 'exact', head: true })
        .eq('option_id', id);
      if ((machineOptionsCount ?? 0) > 0) return 'has_machines';
      const { count: templateOptionsCount } = await supabase
        .from('machine_template_options')
        .select('*', { count: 'exact', head: true })
        .eq('option_id', id);
      if ((templateOptionsCount ?? 0) > 0) return 'has_machines';
      const { error } = await supabase.from('options').delete().eq('id', id);
      if (error) throw error;
      setOptions((prev) => prev.filter((o) => o.id !== id));
      setArchivedOptions((prev) => prev.filter((o) => o.id !== id));
      return 'ok';
    } catch {
      return 'error';
    }
  };

  return {
    options,
    archivedOptions,
    archivedLoaded,
    loadArchived,
    addOption,
    updateOption,
    archiveOption,
    unarchiveOption,
    deleteOption,
  };
}
